import React, { useState, useRef, useCallback, useEffect, createContext, useContext, useMemo, forwardRef } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useIntl } from 'react-intl'
import clsx from 'clsx'
import { v4 as uuidv4 } from 'uuid';
import { CustomSelectMeetingMultipleValue, CustomSelectPipelineMultiValue, VirtualizedSelectOptionPipeline } from '../Pipelines/CustomComponentSelect/CustomSelect'
import { DefaultOption, DefaultSingleValue, ShowGroupLabel, ShowMultiValue, VirtualizedSelectLoading } from './VirtualizedComponentsParts';
import { groupColors } from './VirtualizedConfigData';

const _X__DEBUG__MODE = false

// export const VirtualizedSelectContext = createContext<any | undefined>(undefined)

export interface VirtualizedSelectOption { // child
  id?: string
  label: string
  value: string
  color?: string
  v_id?: string
}

export interface VirtualizedSelectOptionMeeting extends VirtualizedSelectOption {
  dateMeet?: string
  meetingStatus?: string
}

export interface VirtualizedSelectOptionsType {
  id?: string
  v_id?: string
  value?: string
  label: string
  group?: string
  options?: VirtualizedSelectOption[]
  // ============ custom key refactor later ============
  nameCustomField?: string
  name?: string
  nameTemplate?: string
  nameMerchant?: string
  idCustomField?: string
  idTemplate?: string
  idMerchant?: string
  customFieldCompanyCode?: string
  idMerchantLink?: string
  nominal?: number
  dateCreated?: string
}


export interface VirtualizedSelectProps<TOption extends VirtualizedSelectOption = VirtualizedSelectOption> {
  id?: string
  title?: string
  options: VirtualizedSelectOptionsType[]
  value?: VirtualizedSelectOption | VirtualizedSelectOption[] | null
  // onChange: (option: Option | Option[]) => void
  onChange: (option: any) => void
  onInputChange?: (value: string) => void
  onBlur?: () => void
  defaultValue?: VirtualizedSelectOption
  placeholder?: string
  name?: string
  className?: string
  required?: boolean
  openMenuOnClick?: boolean
  closeMenuOnSelect?: boolean
  isMenuOpen?: boolean
  isLoading?: boolean
  isShowGroup?: boolean
  isSelectGroup?: boolean
  isDisabled?: boolean
  isMulti?: boolean
  isClearable?: boolean
  isHideSelectedOption?: boolean
  isReselectable?: boolean
  isSearchable?: boolean
  isSort?: boolean
  sortType?: 'ASC' | 'DESC'
  dialogPosition?: 'top' | 'bottom'
  estimateSize?: number
  overscan?: number
  isMultiValue?: boolean
  typeMultiValue?: 'default' | 'meeting' | 'pipeline'
  defaultComponentConfig?: { // default components only
    isCompactMenu?: boolean
    width?: string
    isMultiValuesMaxHeight?: string
  }
  components?: {
    ClearIndicator?: React.ElementType<any>
    Control?: React.ElementType<any>
    DropdownIndicator?: React.ElementType<any>
    DownChevron?: React.ElementType<any>
    CrossIcon?: React.ElementType<any>
    Group?: React.ElementType<any>
    GroupHeading?: React.ElementType<any>
    IndicatorsContainer?: React.ElementType<any>
    IndicatorSeparator?: React.ElementType<any>
    Input?: React.ElementType<any>
    LoadingIndicator?: React.ElementType<any>
    Menu?: React.ElementType<any>
    MenuList?: React.ElementType<any>
    MenuPortal?: React.ElementType<any>
    LoadingMessage?: React.ElementType<any>
    NoOptionsMessage?: React.ElementType<any>
    MultiValue?: React.ElementType<any>
    MultiValueContainer?: React.ElementType<any>
    MultiValueLabel?: React.ElementType<any>
    MultiValueRemove?: React.ElementType<any>
    Option?: React.ComponentType<{ option: VirtualizedSelectOptionsType; onClick?: () => void }>
    Placeholder?: React.ElementType<any>
    SelectContainer?: React.ElementType<any>
    SingleValue?: React.ComponentType<{ value: VirtualizedSelectOption }>
    ValueContainer?: React.ElementType<any>
  }
}


const VirtualizedSelect = forwardRef<HTMLDivElement, VirtualizedSelectProps>(function VirtualizedSelect(
  {
    id,
    title = '',
    options,
    value,
    onChange,
    onInputChange,
    onBlur,
    defaultValue,
    placeholder = 'Select an option',
    name,
    className = '',
    required = false,
    openMenuOnClick = true,
    closeMenuOnSelect = true,  // only support group options
    isMenuOpen = false,
    isLoading = false,
    isShowGroup = true, // only support group options
    isSelectGroup = false, // only support group options
    isDisabled = false,
    isMulti = false,
    isClearable = true,
    isHideSelectedOption = false, // only support single value not multiple
    isReselectable = false, // only support single value not multiple
    isSearchable = true,
    isSort = true,
    sortType = 'ASC',
    dialogPosition = 'bottom',
    estimateSize = 35, // virtualize option height // Perkiraan tinggi tiap item Option (misal 50px)
    overscan = 5, // virtualize option height // Merender 5 item tambahan di atas dan bawah viewport
    components,
    isMultiValue = false,
    typeMultiValue = 'default',
    defaultComponentConfig = {
      isCompactMenu: true,
      width: '600px'
    }
  },
  ref
) {
  const intl = useIntl()

  const [isOpen, setIsOpen] = useState<boolean | null>(isMenuOpen ? isMenuOpen : false)
  const [isGroup, setIsGroup] = useState(false)
  const [groupList, setGroupList] = useState<{ name: string, color: string }[]>([])
  const [hover, setHover] = useState<string>()
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [filteredGroup, setFilteredGroup] = useState<string>('')
  const [filteredOptions, setFilteredOptions] = useState<VirtualizedSelectOptionsType[]>([])
  const [selectedOption, setSelectedOption] = useState<VirtualizedSelectOption | VirtualizedSelectOption[] | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  const containerRef = useRef<HTMLDivElement>(null)
  const parentRef = useRef<HTMLDivElement>(null)

  // --------------- get options ---------------
  const getOptions = useMemo(() => {
    if (!options) return [];

    // /* ===== */ _X__DEBUG__MODE && console.log('options', options)

    const filteredOptions = options.flatMap((option) => {
      option.v_id = uuidv4()

      if (option.options) {
        return option.options
          .filter((opt) => {
            const matchesSearchTerm = opt.label.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesFilteredGroup = filteredGroup !== '' ? option.label.toLowerCase().includes(filteredGroup.toLowerCase()) : true;
            return matchesSearchTerm && matchesFilteredGroup;
          })
          .map((opt) => ({ ...opt, group: option.label, v_id: uuidv4() }));
      }

      const matchesSearchTerm = option.label.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilteredGroup = filteredGroup ? option.label.toLowerCase().includes(filteredGroup.toLowerCase()) : true;
      return matchesSearchTerm && matchesFilteredGroup ? [option] : [];
    });

    /* ===== */ _X__DEBUG__MODE && console.log('filteredOptions', title, filteredOptions)

    return isSort
      ? filteredOptions.sort((a, b) =>
        sortType === "ASC"
          ? a.label.localeCompare(b.label)
          : b.label.localeCompare(a.label)
      )
      : filteredOptions;
  }, [options, searchTerm, filteredGroup, isSort]);

  // --------------- group options ---------------
  useEffect(() => {
    // console.log({getOptions})
    // console.log({options})
    if (options) {
      const newGroupList = Array.from(
        new Set(options.flatMap((option) => (option.options ? [option.label] : [])))
      ).filter((label): label is string => label !== undefined)
      // console.log({newGroupList})
      setGroupList((prev) => [
        ...prev,
        ...newGroupList.map((label, index) => ({
          name: label,
          color: groupColors[index]?.hex,
        })),
      ])
    } else {

    }
    setFilteredOptions(getOptions)
  }, [options])

  // --------------- check group options ---------------
  useEffect(() => {
    groupList.length > 0 ? setIsGroup(true) : setIsGroup(false)
  }, [groupList])

  // --------------- memorize value ---------------
  const memorizedValue = useMemo(
    () =>
      Array.isArray(value)
        ? value.filter((val) =>
          options.some((option) =>
            option.options
              ? option.options.some((opt) => opt.value === val.value)
              : option.value === val.value
          )
        )
        : value &&
          options.some((option) =>
            option.options
              ? option.options.some((opt) => opt.value === value.value)
              : option.value === value.value
          )
          ? value
          : defaultValue ? defaultValue : null,
    [value, options, defaultValue]
  )

  useEffect(() => {
    setSelectedOption(memorizedValue)
  }, [memorizedValue])

  // useEffect(() => {
  //   console.log('value',value)
  //   console.log('valueselected',selectedOption)
  // }, [value]);

  // --------------- filter options ---------------
  useEffect(() => {
    if (searchTerm.length === 0 || searchTerm === '') {
      onInputChange?.(searchTerm)
      if (Array.isArray(selectedOption) && selectedOption.length > 0 && isMulti) {
        setFilteredOptions(
          getOptions.filter((opt) => {
            return !selectedOption.find((so) => {
              return opt.value === so.value && opt.label === so.label
            })
          })
        )
      } else {
        !Array.isArray(selectedOption) ?
          setFilteredOptions(
            getOptions.filter((opt) => {
              return selectedOption?.value !== opt.value && selectedOption?.label !== opt.label
            })
          ) : setFilteredOptions(getOptions)
      }
    } else {
      setFilteredOptions(getOptions)
    }
  }, [searchTerm])

  // --------------- close dropdown when click outside ---------------
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        /* ===== */ _X__DEBUG__MODE && console.log('handleOutsideClick', isOpen)
        setIsOpen(false)
        setSearchTerm('')
        setFilteredGroup('')
        if (isOpen) {
          onBlur?.()
        }
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [])

  // --------------- navigate with keyboard ---------------
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen || filteredOptions.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((prev) =>
          prev === null || prev === filteredOptions.length - 1 ? 0 : prev + 1
        );
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((prev) =>
          prev === null || prev === 0 ? filteredOptions.length - 1 : prev - 1
        );
      }

      if (e.key === 'Enter' && activeIndex !== null) {
        e.preventDefault();
        handleOptionClick(filteredOptions[activeIndex] as VirtualizedSelectOption);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredOptions, activeIndex]);


  // --------------- virtualize dropdown ---------------
  const rowVirtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: useCallback(() => estimateSize, []),
    overscan
  })

  // --------------- scroll to active index ---------------
  useEffect(() => {
    if (activeIndex !== null) {
      rowVirtualizer.scrollToIndex(activeIndex);
    }
  }, [activeIndex, rowVirtualizer]);

  // --------------- reset active index ---------------
  useEffect(() => {
    /* ===== */ _X__DEBUG__MODE && console.log('VSELECT isOpen', placeholder, isOpen)
    if (!isOpen) setActiveIndex(null);
  }, [isOpen]);

  useEffect(() => {
    setActiveIndex(null);
  }, [filteredOptions]);


  // const toggleDropdown = () => {
  //   console.log('toggleDropDown', isOpen)
  //   if (isOpen) {
  //     onBlur?.()
  //   }
  //   setIsOpen((prev) => !prev)
  // }

  // const toggleDropdown = () => {
  //   console.log('toggleDropDown', isOpen)
  //   if (isOpen) {
  //     onBlur?.()
  //   }
  //   setIsOpen((prev) => !prev)
  // }

  const handleOptionClick = (option: VirtualizedSelectOption) => {
    /* ===== */ _X__DEBUG__MODE && console.log('handleOptionClick', option)

    if (isMulti) {
      setSelectedOption((prev) => {
        const options = Array.isArray(prev) ? prev : [] // Pastikan prev selalu array
        const exists = options.find((opt) => opt.value === option.value)

        return exists ? options.filter((opt) => opt.value !== option.value) : [...options, option]
      })
      onChange(Array.isArray(selectedOption) ? [...selectedOption, option] : ([option] as VirtualizedSelectOption[]))
    } else {
      // Jika single-select
      setSelectedOption(option as VirtualizedSelectOption)
      onChange(option as VirtualizedSelectOption)
    }

    if (closeMenuOnSelect) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }

    if (isHideSelectedOption) setFilteredOptions(getOptions.filter((opt) => opt.value !== option.value && opt.label !== option.label))
    setFilteredGroup('')
    setSearchTerm('')
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  const handleClear = () => {
    setSelectedOption(isMulti ? [] : null)
    setFilteredOptions(getOptions)
    setSearchTerm('')
    setFilteredGroup('')
    onChange(isMulti ? [] : null)
  }

  // useEffect(() => {
  //   if (containerRef.current) {
  //     console.log('containerRef.current.offsetWidth', containerRef.current.offsetWidth)
  //   }
  // }, [containerRef])

  // --------------- set selected option ---------------
  useEffect(() => {
    /* ===== */ _X__DEBUG__MODE && console.log('selectedOption', title, selectedOption)
    if (Array.isArray(selectedOption) && selectedOption.length > 0 && isMulti) {
      /* ===== */ _X__DEBUG__MODE && console.log('getOptions', getOptions)
      setFilteredOptions(
        getOptions.filter((opt) => {
          return !selectedOption.find((so) => {
            return opt.value === so.value && opt.label === so.label
          })
        })
      )
    }
    else {
      setFilteredOptions(getOptions)
    }
  }, [selectedOption])

  // useEffect(() => {
  //   if (isMulti) {
  //     setSelectedOption([])
  //   } else {
  //     console.log('isMulti tidaaak', placeholder, value)
  //     setSelectedOption(null)
  //   }
  // }, [isMulti])

  useEffect(() => {
    /* ===== */ _X__DEBUG__MODE && console.log('options', options)
    if (isGroup && groupList.length > 0 && filteredGroup !== '') {
      /* ===== */ _X__DEBUG__MODE && console.log('filteredGroup', filteredGroup)
      setFilteredOptions(getOptions.filter((opt) => opt && opt.group === filteredGroup))
    } else if (isGroup && groupList.length > 0 && filteredGroup === '') {
      setFilteredOptions(getOptions)
    }
  }, [isGroup, filteredGroup]);

  // --------------- set render components ---------------
  const OptionComponent = components?.Option || DefaultOption
  const SingleValueComponent = components?.SingleValue || DefaultSingleValue

  const RenderComponentOptionValue = () => {
    if (isMultiValue && typeMultiValue === 'meeting') {
      return (
        <CustomSelectMeetingMultipleValue
          selectedOption={selectedOption as VirtualizedSelectOptionMeeting[]}
          handleClose={(v: VirtualizedSelectOption) => {
            setSelectedOption?.((prev) => {
              const updatedOptions = Array.isArray(prev) ? prev.filter((opt) => opt.value !== v.value) : [];
              setFilteredOptions(
                getOptions.filter(
                  (opt) => !updatedOptions.some((so) => so.value === opt.value)
                )
              );
              onChange(updatedOptions);
              return updatedOptions;
            });
          }}
        />
      )
    } else if (isMultiValue && typeMultiValue === 'pipeline') {
      return (
        <CustomSelectPipelineMultiValue
          selectedOption={selectedOption as VirtualizedSelectOptionPipeline[]}
          handleClose={(v: VirtualizedSelectOption) => {
            setSelectedOption?.((prev) => {
              const updatedOptions = Array.isArray(prev) ? prev.filter((opt) => opt.value !== v.value) : [];
              setFilteredOptions(
                getOptions.filter(
                  (opt) => !updatedOptions.some((so) => so.value === opt.value)
                )
              );
              onChange(updatedOptions);
              return updatedOptions;
            });
          }}
        />
      )
    }

    return <ShowMultiValue
      selectedOption={selectedOption as VirtualizedSelectOption[]}
      handleClose={(v: VirtualizedSelectOption) => {
        if (isDisabled) return
        setSelectedOption?.((prev) => {
          const updatedOptions = Array.isArray(prev) ? prev.filter((opt) => opt.value !== v.value) : [];
          setFilteredOptions(
            getOptions.filter(
              (opt) => !updatedOptions.some((so) => so.value === opt.value)
            )
          );
          onChange(updatedOptions);
          return updatedOptions;
        });
      }}
    />
  }

  return (
    <div
      id={id}
      ref={ref}
      className={clsx('position-relative', className)}
    >
      {name && (
        <input
          type="hidden"
          name={name}
          value={
            Array.isArray(value)
              ? value.map((v) => v.value).join(',') // Join multiple values for isMulti
              : value?.value || '' // Single value or empty string
          }
          required={required}
        />
      )}
      <div
        ref={containerRef}
      >
        {isOpen ? (
          <>
            <div
              className='d-flex flex-row border border-2 border-secondary justify-content-between rounded'
            >
              <div className='d-flex flex-row flex-wrap align-items-between justify-content-start p-2 w-100 gap-2'>
                {isMulti && Array.isArray(selectedOption) && selectedOption.length > 0 ? (
                  <RenderComponentOptionValue />
                ) : (
                  <div className='d-flex justify-content-between p-2 text-truncate w-100'>
                    {typeof selectedOption === 'object' ? (
                      <SingleValueComponent
                        value={selectedOption as VirtualizedSelectOption}
                        placeholder={placeholder}
                      />
                    ) : (
                      <span>{placeholder}</span>
                    )}
                  </div>
                )}
                <div className="d-flex justify-content-between w-100">
                  {groupList.length > 0 && isSelectGroup && isGroup && isShowGroup && (() => {
                    const groupListOptions = groupList.map((group) => ({
                      label: group.name,
                      value: group.name,
                    }));

                    return (
                      <div className='d-flex justify-content-between rounded gap-2 mx-2' style={{ outline: '2px solid rgb(165, 165, 165)' }}>
                        <select id="groupList" name="groupList" className='bg-transparent border border-0 px-2' value={filteredGroup} onChange={(e) => setFilteredGroup(e.target.value)} style={{ outline: 'none' }}>
                          <option value="" disabled selected>
                            {intl.formatMessage({ id: 'FORM.LABEL.CHOOSE' }) + ' Option Group'}
                          </option>
                          {groupListOptions.map((option) => (
                            <option key={option.value} value={option.value} className='text-break'>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {filteredGroup !== '' && (
                          <div className='d-flex align-items-center h-100 justify-content-center w-100 cursor-pointer pe-3 ps-2'
                            onClick={() => {
                              setFilteredGroup('')
                            }}
                          >
                            <i className="bi bi-x-lg"
                              style={{ fontSize: '1rem', fontWeight: 'bold' }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {isSearchable &&
                    <input
                      type='text'
                      className='form-control border border-0 w-100'
                      placeholder='Search...'
                      value={searchTerm}
                      onChange={handleSearch}
                      autoFocus
                    />
                  }
                </div>
              </div>

              <div className='d-flex align-content-center p-2 position-relative'>
                {isClearable && selectedOption && (
                  <div className='d-flex align-items-center h-100 cursor-pointer gap-4 px-2'>
                    <i onClick={handleClear} className='align-middle p-2 bi bi-x-lg fw-bold' />
                    <i onClick={() => { setIsOpen(false) }} className='p-4 bi bi-chevron-up fw-bold' />
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <div
              className={`d-flex align-items-center justify-content-between gap-1 px-3 py-2 border border-2 rounded bg-white `}
              onClick={isDisabled ? undefined : () => {
                if (openMenuOnClick) {
                  setIsOpen(true)
                } else {
                  setIsOpen(false)
                }
              }
              }
              style={{
                cursor: isDisabled ? 'not-allowed' : 'pointer', minWidth: '150px', backgroundColor:
                  isDisabled ? '#f0f0f0 !important' : 'transparent',
              }}
            >
              {Array.isArray(selectedOption) ? ( // Multiple values
                <div
                  className='d-flex flex-wrap align-items-center align-items-stretch justify-content-start gap-2'
                  style={{
                    maxHeight: defaultComponentConfig.isMultiValuesMaxHeight || 'auto',
                    overflowY: defaultComponentConfig.isMultiValuesMaxHeight ? 'auto' : 'visible'
                  }}
                >
                  {selectedOption.length > 0 ?
                    (
                      isMultiValue && typeMultiValue === 'meeting' ? <CustomSelectMeetingMultipleValue
                        selectedOption={selectedOption}
                        handleClose={(v: VirtualizedSelectOption) => {
                          setSelectedOption?.((prev) => {
                            const updatedOptions = Array.isArray(prev) ? prev.filter((opt) => opt.value !== v.value) : [];

                            setFilteredOptions(
                              getOptions.filter(
                                (opt) => !updatedOptions.some((so) => so.value === opt.value)
                              )
                            );
                            onChange(updatedOptions);
                            return updatedOptions;
                          });
                        }}
                      /> : isMultiValue && typeMultiValue === 'pipeline' ? <CustomSelectPipelineMultiValue
                        selectedOption={selectedOption as VirtualizedSelectOptionPipeline[]}
                        handleClose={(v: VirtualizedSelectOption) => {
                          setSelectedOption?.((prev) => {
                            const updatedOptions = Array.isArray(prev) ? prev.filter((opt) => opt.value !== v.value) : [];

                            setFilteredOptions(
                              getOptions.filter(
                                (opt) => !updatedOptions.some((so) => so.value === opt.value)
                              )
                            );
                            onChange(updatedOptions);
                            return updatedOptions;
                          });
                        }}
                      />
                        : (
                          <div className='d-flex flex-row flex-wrap align-items-between justify-content-start w-100 gap-2'>
                            <ShowMultiValue
                              selectedOption={selectedOption}
                              handleClose={(v: VirtualizedSelectOption) => {
                                setSelectedOption?.((prev) => {
                                  const updatedOptions = Array.isArray(prev) ? prev.filter((opt) => opt.value !== v.value) : [];

                                  setFilteredOptions(
                                    getOptions.filter(
                                      (opt) => !updatedOptions.some((so) => so.value === opt.value)
                                    )
                                  );
                                  onChange(updatedOptions);
                                  return updatedOptions;
                                });
                              }}

                            />
                          </div>
                        )
                    ) : (
                      <span className='text-truncate w-100 pl-3' style={{ color: '#8c8c8b' }}>
                        {placeholder}
                      </span>
                    )}
                </div>
              ) : (
                <>
                  {typeof selectedOption === 'object' ? (
                    <SingleValueComponent
                      value={selectedOption as VirtualizedSelectOption}
                      placeholder={placeholder}
                    />
                  ) : (
                    <span>{placeholder}</span>
                  )}
                </>
                // <span className='d-flex align-items-center bg-warning justify-content-start text-truncate w-100 pl-3'>
                //   {selectedOption?.label || placeholder}
                // </span>
              )}
              {isLoading ?
                <>
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </>
                :
                <div className='d-flex align-items-center cursor-pointer px-2 h-100 gap-4'>
                  {isClearable && selectedOption && (
                    <i onClick={handleClear} className='align-middle p-2 bi bi-x-lg fw-bold' />
                  )}
                  <div
                    onClick={isDisabled ? undefined : () => {
                      if (openMenuOnClick) {
                        setIsOpen(true)
                      } else {
                        setIsOpen(false)
                      }
                    }
                    }
                    // className="bg-danger"
                    style={{ position: 'relative' }}
                  >
                    <i
                      className="bi bi-chevron-down fw-bold"
                      style={{
                        pointerEvents: 'none',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                      }}
                    />
                  </div>
                </div>
              }
            </div>
          </>
        )}

        {isOpen && (
          <div
            className={`position-absolute mt-1 bg-white border rounded-1 shadow`}
            style={{
              zIndex: 1000,
              width: !defaultComponentConfig.isCompactMenu && containerRef.current?.offsetWidth
                ? containerRef.current.offsetWidth > 300
                  ? '100%'
                  : defaultComponentConfig.width
                : '100%',
              bottom: dialogPosition === 'top' ? '100px' : undefined,
            }}
          >
            <div
              ref={parentRef}
              // className='overflow-auto'
              style={{
                maxHeight: '320px',
                width: !defaultComponentConfig.isCompactMenu && containerRef.current?.offsetWidth
                  ? containerRef.current.offsetWidth > 300
                    ? 'auto'
                    : defaultComponentConfig.width
                  : 'auto',
                overflowX: !defaultComponentConfig.isCompactMenu && containerRef.current?.offsetWidth
                  ? containerRef.current.offsetWidth > 300
                    ? 'hidden'
                    : 'scroll'
                  : 'hidden',
                overflowY: 'auto',
              }}
            >
              {isLoading ?
                <VirtualizedSelectLoading />
                :
                <>
                  <div
                    style={{
                      minHeight: `${rowVirtualizer.getTotalSize()}px`,
                      position: 'relative',
                    }}
                  >
                    {filteredOptions.length === 0 ? (
                      <div className='d-flex align-items-center h-100 justify-content-center p-8 w-100 gap-4'>
                        <i className='bi bi-emoji-frown' style={{ fontSize: '2rem' }} />{' '}
                        <span style={{ fontSize: '1.5rem' }}>
                          {intl.formatMessage({ id: 'FORM.CONFIRM.DATA_NOT_FOUND' }, { title })}
                        </span>
                      </div>
                    ) : (
                      rowVirtualizer.getVirtualItems().map((virtualItem, index) => {
                        // /* ===== */ _X__DEBUG__MODE && console.log('rowVirtualizer.getVirtualItems()',rowVirtualizer.getVirtualItems())
                        /* ===== */ _X__DEBUG__MODE && console.log('virtualItem', virtualItem, title)
                        const option = filteredOptions[virtualItem.index]
                        if (!option) return null
                        const selectedIndex =
                          typeof selectedOption === 'object' && selectedOption !== null
                            ? (selectedOption as VirtualizedSelectOption).v_id
                              ? (selectedOption as VirtualizedSelectOption).v_id === option.v_id
                              : (selectedOption as VirtualizedSelectOption).value === option.value && (selectedOption as VirtualizedSelectOption).label === option.label
                            : false;


                        {/* =========================== Option Item =========================== */ }
                        return (
                          <div
                            key={virtualItem.index}
                            className={`position-absolute 
                              w-100`}
                            style={{
                              top: `${virtualItem.start}px`,
                              height: `${virtualItem.size}px`,
                              width: containerRef.current?.offsetWidth,
                              background:
                                activeIndex === virtualItem.index
                                  ? '#d9f7be'
                                  // : hover === virtualItem.index.toString() &&
                                  //   (!Array.isArray(selectedOption) ||
                                  //     !selectedOption.some((opt) => opt.value === option.value))
                                  // ? '#b1d9fa'
                                  : Array.isArray(selectedOption) &&
                                    selectedOption.some((opt) => opt.value === option.value)
                                    ? '#c9c9c9'
                                    : 'transparent',
                            }}
                            onClick={() => {
                              if (Array.isArray(selectedOption)) {
                                if (!selectedOption.some((opt) => opt.value === option.value)) {
                                  handleOptionClick(option as VirtualizedSelectOption)
                                }
                              } else {
                                handleOptionClick(option as VirtualizedSelectOption)
                              }
                            }}
                          >
                            <div
                              onMouseEnter={() => setHover(index.toString())}
                              onMouseLeave={() => setHover('')}
                              className={`d-flex align-items-center w-100 h-100 `}
                              style={{
                                background:
                                  hover === index.toString() &&
                                    (!Array.isArray(selectedOption) ||
                                      !selectedOption.some((opt) => opt.value === option.value))
                                    ? '#b1d9fa'
                                    : Array.isArray(selectedOption) &&
                                      selectedOption.some((opt) => opt.value === option.value)
                                      ? '#c9c9c9'
                                      : 'transparent',
                              }}
                            >
                              <div
                                className={`d-flex align-items-center justify-content-between w-100 h-100 ${isGroup ? 'px-2' : 'p-0'
                                  }`}
                                style={{
                                  backgroundColor: selectedIndex ? '#31abff' : 'transparent',
                                  color: selectedIndex ? '#fff' : 'black',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitBoxOrient: 'vertical',
                                  WebkitLineClamp: 2,
                                  padding: '0'
                                }}
                              >
                                <OptionComponent
                                  key={option.value}
                                  option={option}
                                  onClick={() => { }}
                                />
                                {isShowGroup && isGroup && (
                                  <ShowGroupLabel groupList={groupList} option={option} />
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      })
                    )}

                  </div>
                </>
              }
            </div>
          </div>
        )}
      </div>
    </div>
  )
});

export default VirtualizedSelect;

